import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject, generateRedirectUrl } from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'
import useProviderPriceList, { useProviderPriceListForm } from '@hooks/useProviderPriceList'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { generateUrls as generateProviderUrls } from '@hooks/useProvider'

import EntitySelector from '@components/EntitySelector'

const MODAL_KEY = 'CreateOrEditProviderPriceListModal'

type ModalCallbacks = {
  closeModal: () => void,
  createProviderPriceList: (payload: HandleCallbackActionParams) => void,
  updateProviderPriceList: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditProviderPriceListModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createProviderPriceList, updateProviderPriceList } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const providerPriceList = digObject(modalPayload, 'providerPriceList', {})

  const newRecord = !providerPriceList.id

  const {
    callbacks: {
      createProviderPriceList: createFn,
      updateProviderPriceList: updateFn,
    },
    creating,
    updating,
  } = useProviderPriceList(providerPriceList)

  const {
    entityState,
    entityState: {
      is_master,
      master_price_list_id,
      provider_id,
      service_id,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useProviderPriceListForm(providerPriceList, { validateOn: 'change' })

  const { editProviderUrl } = generateProviderUrls({ id: provider_id })

  const createOrUpdateProviderPriceListPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: ` ${newRecord ? 'Created' : 'Updated'}`,
  }

  const { isProviderDirector } = useCurrentUser()

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Price List" />

      <SidebarModal.Content>
        <Form>
          {!isProviderDirector && (
            <EntitySelector
              callbacks={{
                selectItem: provider => setEntityState({
                  provider_id: provider?.id,
                  service_id: undefined,
                }),
              }}
              entityKey="providers"
              goToHref={`${editProviderUrl}?redirect=${generateRedirectUrl()}`}
              label="* Provider"
              selectedItemId={provider_id}
            />
          )}

          <EntitySelector
            boxProps={{ marginTop: !isProviderDirector ? 'large' : 'none' }}
            callbacks={{
              selectItem: service => setEntityState({ service_id: service?.id }),
            }}
            entityKey="services"
            label="* Service"
            selectedItemId={service_id}
            searchRequestOptions={{ assigned_provider_id: provider_id }}
          />

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Master" errorMessage={errors.is_master} marginTop="large">
            <select
              name="is_master"
              onChange={e => setEntityState({
                is_master: e.target.value === 'true',
                master_price_list_id: undefined,
              })}
              value={is_master}
            >
              <option value="">Please Select...</option>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          {!is_master && (
            <EntitySelector
              boxProps={{ marginTop: 'large' }}
              callbacks={{
                selectItem: priceList => setEntityState({ master_price_list_id: priceList?.id }),
              }}
              entityKey="providerPriceLists"
              label="* Master Price List"
              selectedItemId={master_price_list_id}
              searchRequestOptions={{ is_master: true, provider_id, service_id }}
            />
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createProviderPriceList(createOrUpdateProviderPriceListPayload)
              : () => updateProviderPriceList(createOrUpdateProviderPriceListPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} ` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderPriceListModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
